import * as THREE from 'three';
import { MASK } from '../../world/Physics.js';
import { MEANS, TEAM } from '../MatchAuthority.js';

/**
 * Shared machinery for every scorestreak.
 *
 * Streak modules are small and there are seven of them, so anything more than one
 * of them needs lives here rather than being copy-pasted: the damage contract, the
 * target queries, the shared materials, and the two pooled visual effects (tracers
 * and billboard sprites) that several streaks draw.
 *
 * Everything in this file is allocation-free on the hot path. The pools are built
 * once, lazily, on first use — a player who never earns a Sentry never pays for the
 * tracer buffer.
 */

export const TUNING = {
  /** Tracer beam pool. Shared by the sentry, the helo and the gunship. */
  tracerCount: 64,
  tracerLife: 0.055,
  tracerRadius: 0.055,

  /** Billboard sprite pool. Shared by threat markers, fire and smoke. */
  spriteCount: 320,

  /** Radial damage falls off linearly to zero at the radius. */
  splashMinFraction: 0.15,
};

export const SOLID = MASK.WORLD | MASK.PROP;

/* --------------------------------- scratch -------------------------------- */

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

/* ------------------------------ participation ----------------------------- */

let _streakSeq = 0;

/**
 * Enrol a streak as a *combatant* — something that can shoot and be credited, but
 * that nothing shoots back at.
 *
 * Streaks were previously anonymous: they passed `{ source: this }` into damage
 * calls that then ignored it, and the only thing keeping a sentry's bullets off its
 * owner was a literal comparison against `ctx.player`. Giving them an id and a side
 * is what makes "the sentry killed him, so *he* gets the point" expressible, and
 * what stops a gunship's rockets from treating its owner as a valid target the
 * moment the special case for the player goes away.
 *
 * They register with no `health`, so `requestDamage` against one is refused for
 * being worth zero — which is the behaviour they have always had.
 */
export function registerStreak(ctx, entity, name = 'STREAK', team = TEAM.ALPHA) {
  entity.id ??= `streak_${name.toLowerCase()}_${_streakSeq++}`;
  // `owner` is what makes a sentry's kill *the player's* kill rather than merely
  // their team's — the distinction a squadmate's kill has to fall on the far side of.
  ctx.match?.register(entity, { team, kind: 'streak', name, owner: ctx.player ?? null });
  return entity;
}

/** Remove a streak from the registry when it leaves the map. */
export function unregisterStreak(ctx, entity) {
  ctx.match?.unregister(entity?.id);
}

/* -------------------------------- queries --------------------------------- */

/**
 * Everything alive that `source` may shoot at.
 *
 * With a source and a match registry this is a real hostility test, which is what
 * stops a sentry gun from tracking its own team the moment there are two of them.
 * Without either it falls back to what this always did — "anything that is not the
 * player" — so a streak still works if the AI module or the match is not present.
 */
export function livingEnemies(ctx, out = [], source = null) {
  out.length = 0;
  const match = ctx.match;
  if (match && source) {
    for (const reg of match.actors.values()) {
      if (reg.dead || !reg.actor?.position) continue;
      if (reg.actor === source) continue;
      if (!match.hostile(source, reg.actor)) continue;
      out.push(reg.actor);
    }
    return out;
  }
  const list = ctx.ai?.enemies;
  if (Array.isArray(list)) {
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (e && !e.dead && e.position) out.push(e);
    }
    return out;
  }
  const actors = ctx.physics?.actors;
  if (actors) {
    for (const a of actors.values()) {
      if (a && a.id !== 'player' && !a.dead && a.position) out.push(a);
    }
  }
  return out;
}

/** Centre of mass, which is what everything aims at. Writes into `out`. */
export function aimPointOf(actor, out) {
  return out.set(actor.position.x, actor.position.y + 1.05, actor.position.z);
}

/** Unobstructed line between two world points. */
export function clearLine(ctx, from, to) {
  const phys = ctx.physics;
  if (!phys) return true;
  if (phys.visible) return phys.visible(from, to);
  const d = _c.subVectors(to, from);
  const dist = d.length();
  if (dist < 1e-4) return true;
  d.multiplyScalar(1 / dist);
  return !phys.raycast(from, d, dist - 0.05, SOLID).hit;
}

/**
 * Nearest visible hostile to `from` inside a cone. `forward` may be null for an
 * omnidirectional search. Returns the actor or null.
 */
export function acquire(ctx, from, range, forward = null, cosHalfAngle = -1, scratch = [], source = null) {
  const list = livingEnemies(ctx, scratch, source);
  let best = null;
  let bestDist = range;
  for (let i = 0; i < list.length; i++) {
    const e = list[i];
    aimPointOf(e, _a);
    const dist = _a.distanceTo(from);
    if (dist > bestDist) continue;
    if (forward) {
      _b.subVectors(_a, from).multiplyScalar(1 / Math.max(dist, 1e-4));
      if (_b.dot(forward) < cosHalfAngle) continue;
    }
    if (!clearLine(ctx, from, _a)) continue;
    best = e;
    bestDist = dist;
  }
  return best;
}

/** Height of the floor under (x, z), searching down from `fromY`. */
export function groundUnder(ctx, x, z, fromY = 60, maxDist = 120) {
  const phys = ctx.physics;
  if (!phys) return 0;
  _a.set(x, fromY, z);
  _b.set(0, -1, 0);
  const hit = phys.raycast(_a, _b, maxDist, SOLID);
  if (hit.hit) return hit.point.y;
  // The ray can miss where the level is built from merged batches; the collider
  // table still knows the answer.
  return phys.groundHeight ? phys.groundHeight(x, z, fromY, maxDist) : 0;
}

/* --------------------------------- damage --------------------------------- */

/**
 * One hitscan round, all the way through the contract in ARCHITECTURE.md:
 * raycast via physics, damage via `match.requestDamage`, then the matching
 * `hit:actor` / `hit:surface` so decals, sparks and the minimap all react.
 *
 * Deliberately does NOT emit `weapon:fire` — that event means "the player's gun
 * went off" and owns the tracer/muzzle pipeline and the world audio duck. Streak
 * weapons draw their own tracers instead (see `tracers()`).
 */
export function fireRound(ctx, origin, dir, range, damage, opts = {}) {
  const hit = ctx.physics.raycast(origin, dir, range, MASK.ALL, opts.ignore ?? null);
  if (!hit.hit) return null;

  if (hit.actor) {
    /**
     * A friendly body stops the round but never takes it.
     *
     * That used to be the literal `hit.actor !== ctx.player`, which meant a streak
     * weapon's idea of "friendly" was "the one human". The match decides it now, so
     * `opts.source` — the turret, the helicopter — is what makes the round friendly
     * or not, and the same turret dropped for the other team will refuse the other
     * way round without this function changing.
     */
    const res = ctx.match?.requestDamage({
      target: hit.actor,
      source: opts.source ?? null,
      amount: damage,
      hitbox: hit.hitbox,
      point: hit.point,
      normal: hit.normal,
      dir: dir.clone(),
      means: MEANS.BULLET,
      weapon: opts.weapon ?? null,
    });
    if (res && !res.blocked) {
      ctx.events.emit('hit:actor', {
        actor: hit.actor,
        point: hit.point,
        normal: hit.normal,
        damage: res.amount,
        headshot: res.headshot,
        dir: dir.clone(),
        hitbox: hit.hitbox,
        source: opts.source ?? null,
      });
    }
  } else {
    ctx.events.emit('hit:surface', {
      point: hit.point,
      normal: hit.normal,
      material: hit.materialId,
      entity: null,
      dir: dir.clone(),
    });
  }
  return hit;
}

/**
 * An explosion: the FX event the camera rig and post stack already listen for,
 * the registered `explosion` sound, and line-of-sight radial damage.
 *
 * This used to be two loops — hostiles, then a hand-written special case for the
 * player at a reduced rate. That shape is exactly why `playerScale` had to exist as
 * a parameter: there was no other way to express "this blast should not hurt the one
 * human". Now it is a single sweep over everyone alive, and `playerScale` means what
 * it always meant behind the scenes: **what the blast costs its own side**, the
 * thrower included. A gunship passing 0 keeps its rockets safe around its owner
 * exactly as before.
 */
export function explode(ctx, position, { power = 1, radius = 7, damage = 130, playerScale = 0.55, source = null, scratch = [] } = {}) {
  ctx.events.emit('fx:explosion', { position: position.clone(), power });
  ctx.events.emit('audio:play', { id: 'explosion', position: position.clone(), volume: 0.95 });

  const match = ctx.match;
  if (!match) return;

  const srcTeam = source ? match.teamOf(source) : null;
  for (const reg of match.living()) {
    const a = reg.actor;
    if (!a.position) continue;
    aimPointOf(a, _a);
    const d = _a.distanceTo(position);
    if (d > radius) continue;
    if (!clearLine(ctx, position, _a)) continue;

    const friendly = source != null && (a === source || reg.team === srcTeam);
    const scale = friendly ? playerScale : 1;
    if (scale <= 0) continue;

    const falloff = Math.max(TUNING.splashMinFraction, 1 - d / radius);
    _b.subVectors(_a, position).normalize();
    match.requestDamage({
      target: a,
      source,
      amount: damage * falloff * scale,
      hitbox: null,
      point: _a.clone(),
      normal: _b.clone().negate(),
      dir: _b.clone(),
      means: MEANS.EXPLOSIVE,
      // Your own frag is still your own frag with friendly fire off.
      splash: true,
    });
  }
}

/* -------------------------------- materials -------------------------------- */

const _mats = new Map();

/** Shared, procedurally-parameterised materials. One instance per key, ever. */
export function mat(key) {
  let m = _mats.get(key);
  if (m) return m;
  switch (key) {
    case 'hull':
      m = new THREE.MeshStandardMaterial({ color: 0x3a4149, roughness: 0.72, metalness: 0.45 });
      break;
    case 'hullDark':
      m = new THREE.MeshStandardMaterial({ color: 0x1e242a, roughness: 0.85, metalness: 0.3 });
      break;
    case 'olive':
      m = new THREE.MeshStandardMaterial({ color: 0x4a5340, roughness: 0.9, metalness: 0.1 });
      break;
    case 'crate':
      m = new THREE.MeshStandardMaterial({ color: 0x6c7a4a, roughness: 0.95, metalness: 0.05 });
      break;
    case 'glass':
      m = new THREE.MeshStandardMaterial({
        color: 0x0a1218, roughness: 0.15, metalness: 0.9, emissive: 0x102028, emissiveIntensity: 0.6,
      });
      break;
    case 'accent':
      m = new THREE.MeshBasicMaterial({ color: 0xff8a3d });
      break;
    case 'accentDim':
      m = new THREE.MeshBasicMaterial({ color: 0xb35a20 });
      break;
    case 'hostile':
      m = new THREE.MeshBasicMaterial({ color: 0xff4b3e });
      break;
    case 'tracer':
      m = new THREE.MeshBasicMaterial({
        color: 0xffc27a, blending: THREE.AdditiveBlending, transparent: true,
        depthWrite: false, opacity: 0.95, toneMapped: false,
      });
      break;
    default:
      m = new THREE.MeshStandardMaterial({ color: 0x808080 });
  }
  _mats.set(key, m);
  return m;
}

/* --------------------------------- textures -------------------------------- */

const _texes = new Map();

/**
 * Procedural sprite textures. No binary assets ever enter this project, so every
 * one of these is a 64 px canvas drawn at first use.
 */
export function tex(kind) {
  let t = _texes.get(kind);
  if (t) return t;
  const S = 64;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d');

  if (kind === 'glow' || kind === 'fire' || kind === 'smoke') {
    const grad = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    if (kind === 'fire') {
      grad.addColorStop(0, 'rgba(255,246,214,1)');
      grad.addColorStop(0.25, 'rgba(255,176,64,0.92)');
      grad.addColorStop(0.6, 'rgba(214,74,18,0.42)');
      grad.addColorStop(1, 'rgba(60,14,0,0)');
    } else if (kind === 'smoke') {
      grad.addColorStop(0, 'rgba(190,190,190,0.55)');
      grad.addColorStop(0.55, 'rgba(120,120,120,0.28)');
      grad.addColorStop(1, 'rgba(60,60,60,0)');
    } else {
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.4, 'rgba(255,255,255,0.5)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
    }
    g.fillStyle = grad;
    g.fillRect(0, 0, S, S);
  } else if (kind === 'threat') {
    // A hollow diamond with a dark keyline — the same outline-first logic the HUD
    // uses, because this is drawn over unpredictable world colour.
    g.translate(S / 2, S / 2);
    g.rotate(Math.PI / 4);
    g.lineJoin = 'miter';
    g.strokeStyle = 'rgba(6,3,3,0.9)';
    g.lineWidth = 11;
    g.strokeRect(-16, -16, 32, 32);
    g.strokeStyle = '#ff4b3e';
    g.lineWidth = 6;
    g.strokeRect(-16, -16, 32, 32);
    g.fillStyle = 'rgba(255,75,62,0.28)';
    g.fillRect(-13, -13, 26, 26);
  }

  t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  _texes.set(kind, t);
  return t;
}

/* ------------------------------- tracer pool ------------------------------- */

let _tracers = null;

/**
 * Pooled beam tracers, one InstancedMesh for the whole system. Unused instances
 * are parked at zero scale rather than being removed, so the instance buffer is
 * only ever rewritten for beams that are actually alive.
 */
class TracerPool {
  constructor(ctx) {
    this.ctx = ctx;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    geo.translate(0, 0, 0.5); // origin at the near end, extending along +Z
    this.mesh = new THREE.InstancedMesh(geo, mat('tracer'), TUNING.tracerCount);
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.renderOrder = 3;
    this.mesh.name = 'streak-tracers';
    ctx.scene.add(this.mesh);

    this.slots = new Array(TUNING.tracerCount);
    for (let i = 0; i < TUNING.tracerCount; i++) {
      this.slots[i] = { life: 0, len: 0 };
      _m.makeScale(0, 0, 0);
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    this.cursor = 0;
    this.alive = 0;
    this.mesh.visible = false;
  }

  fire(from, to) {
    const i = this.cursor++ % TUNING.tracerCount;
    const slot = this.slots[i];
    if (slot.life <= 0) this.alive++;
    slot.life = TUNING.tracerLife;
    const len = from.distanceTo(to);
    slot.len = len;
    _m.lookAt(from, to, _up);
    _q.setFromRotationMatrix(_m);
    _s.set(TUNING.tracerRadius, TUNING.tracerRadius, len);
    // lookAt orients -Z toward the target; the geometry runs along +Z, so flip.
    _q.multiply(_flipQ);
    _m.compose(from, _q, _s);
    this.mesh.setMatrixAt(i, _m);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.visible = true;
  }

  update(dt) {
    if (this.alive === 0) return;
    let dirty = false;
    for (let i = 0; i < TUNING.tracerCount; i++) {
      const slot = this.slots[i];
      if (slot.life <= 0) continue;
      slot.life -= dt;
      if (slot.life <= 0) {
        _m.makeScale(0, 0, 0);
        this.mesh.setMatrixAt(i, _m);
        this.alive--;
        dirty = true;
      }
    }
    if (dirty) this.mesh.instanceMatrix.needsUpdate = true;
    if (this.alive === 0) this.mesh.visible = false;
  }
}

const _flipQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);

export function tracers(ctx) {
  if (!_tracers) _tracers = new TracerPool(ctx);
  return _tracers;
}

/** Ticks the tracer pool only if something has ever built it. Idle cost: one test. */
export function updateTracers(dt) {
  if (_tracers) _tracers.update(dt);
}

/* ------------------------------- sprite pool ------------------------------- */

/**
 * A pooled billboard layer. One `THREE.Points` per texture kind, which is one draw
 * call regardless of how many puffs of fire or threat markers are on screen.
 * Points billboard on the GPU for free, which is the whole reason to use them here
 * instead of sprites.
 */
class SpriteLayer {
  constructor(ctx, kind, count, { size = 1.4, color = 0xffffff, rgb = null, additive = true, depthTest = true, opacity = 1 } = {}) {
    this.count = count;
    this.pos = new Float32Array(count * 3);
    this.alphaAttr = new Float32Array(count);
    this.sizeAttr = new Float32Array(count);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aAlpha', new THREE.BufferAttribute(this.alphaAttr, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aSize', new THREE.BufferAttribute(this.sizeAttr, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setDrawRange(0, 0);

    const m = new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: tex(kind) },
        // `rgb` accepts values above 1: the frame is tone-mapped downstream, so an
        // emissive effect authored in LDR comes out muddy.
        uColor: { value: rgb ? new THREE.Color(rgb[0], rgb[1], rgb[2]) : new THREE.Color(color) },
        uScale: { value: size },
        uOpacity: { value: opacity },
      },
      vertexShader: /* glsl */ `
        attribute float aAlpha;
        attribute float aSize;
        varying float vAlpha;
        uniform float uScale;
        void main() {
          vAlpha = aAlpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = uScale * aSize * 300.0 / max(-mv.z, 0.001);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform sampler2D uMap;
        uniform vec3 uColor;
        uniform float uOpacity;
        varying float vAlpha;
        void main() {
          vec4 t = texture2D(uMap, gl_PointCoord);
          gl_FragColor = vec4(uColor * t.rgb, t.a * vAlpha * uOpacity);
          if (gl_FragColor.a < 0.004) discard;
        }`,
      transparent: true,
      depthWrite: false,
      depthTest,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      toneMapped: false,
    });

    this.points = new THREE.Points(geo, m);
    this.points.frustumCulled = false;
    this.points.renderOrder = depthTest ? 2 : 6;
    this.points.visible = false;
    this.points.name = `streak-${kind}`;
    ctx.scene.add(this.points);
    this.n = 0;
  }

  begin() {
    this.n = 0;
  }

  add(x, y, z, alpha, size) {
    if (this.n >= this.count) return;
    const i = this.n++;
    this.pos[i * 3] = x;
    this.pos[i * 3 + 1] = y;
    this.pos[i * 3 + 2] = z;
    this.alphaAttr[i] = alpha;
    this.sizeAttr[i] = size;
  }

  end() {
    const g = this.points.geometry;
    g.setDrawRange(0, this.n);
    if (this.n > 0) {
      g.attributes.position.needsUpdate = true;
      g.attributes.aAlpha.needsUpdate = true;
      g.attributes.aSize.needsUpdate = true;
    }
    this.points.visible = this.n > 0;
  }
}

const _layers = new Map();

export function spriteLayer(ctx, kind, opts) {
  let l = _layers.get(kind);
  if (!l) {
    l = new SpriteLayer(ctx, opts?.texture ?? kind, opts?.count ?? TUNING.spriteCount, opts);
    _layers.set(kind, l);
  }
  return l;
}

/* --------------------------------- helpers -------------------------------- */

export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/** A point `dist` metres ahead of the player, dropped onto the floor. */
export function inFrontOfPlayer(ctx, dist, out = new THREE.Vector3()) {
  const p = ctx.player;
  const yaw = p?.yaw ?? 0;
  const px = (p?.position?.x ?? 0) - Math.sin(yaw) * dist;
  const pz = (p?.position?.z ?? 0) - Math.cos(yaw) * dist;
  const from = _a.set(px, (p?.position?.y ?? 0) + 1.6, pz);
  const down = _b.set(0, -1, 0);
  const hit = ctx.physics.raycast(from, down, 30, SOLID);
  return out.set(px, hit.hit ? hit.point.y : (p?.position?.y ?? 0), pz);
}

/** Where the player is looking, out to `range`. Falls back to a point in the air. */
export function playerAimPoint(ctx, range = 200, out = new THREE.Vector3()) {
  const cam = ctx.camera;
  cam.getWorldDirection(_a);
  const origin = _b.copy(cam.position);
  const hit = ctx.physics.raycast(origin, _a, range, SOLID);
  if (hit.hit) return out.copy(hit.point);
  return out.copy(origin).addScaledVector(_a, range);
}

export function disposeShared() {
  if (_tracers) {
    _tracers.mesh.parent?.remove(_tracers.mesh);
    _tracers.mesh.geometry.dispose();
    _tracers = null;
  }
  for (const l of _layers.values()) {
    l.points.parent?.remove(l.points);
    l.points.geometry.dispose();
    l.points.material.dispose();
  }
  _layers.clear();
}
